import Controller from '@ember/controller';
import type SessionService from 'editor/services/session';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { scheduleOnce } from '@ember/runloop';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isDevelopingApp, macroCondition } from '@embroider/macros';
import { tracked } from '@glimmer/tracking';
import { RenameProjectCommand } from 'editor/models/project-commands';
import type ProjectManagerService from 'editor/services/project-manager';
import { SongFile, SongFolder } from 'editor/services/project-manager';
import type UndoManagerService from 'editor/services/undo-manager';
import nameFilter from 'editor/utils/name-filter';

import { AddFolderCommand } from 'editor/models/folder-commands';

interface CarouselItemData {
  href: string;
  imgSrc: string;
  alt: string;
  isNew?: boolean;
}

const S3_BASE_URL = 'https://bbmo-carousel-content.s3.amazonaws.com/';
const S3_IMAGES_BASE_URL = S3_BASE_URL + 'images/';
const CAROUSEL_JSON_URL_PROD = S3_BASE_URL + 'carousel.json';
const CAROUSEL_JSON_URL_DEV = S3_BASE_URL + 'carousel-dev.json';

function waitForSongs(folder: SongFolder): Promise<void> {
  return new Promise((resolve) => {
    const checkSongs = () => {
      if (folder.songs.length > 0) {
        resolve();
      } else {
        setTimeout(checkSongs, 100);
      }
    };
    checkSongs();
  });
}

export default class RestrictedController extends Controller {
  @service declare session: SessionService;
  logout = () => this.session.logout();
  @service declare projectManager: ProjectManagerService;
  @service declare undoManager: UndoManagerService;
  @service declare router: RouterService;
  @tracked displayHelpPopup = false;
  @tracked showProjectMenu = false;
  @tracked showOptionsMenu?: boolean;
  @tracked showConversionModal = false;
  @tracked showSkipBackupModal = false;
  TOS_KEY = 'bbmo_tos_agreed';
  @tracked tosError: string | null = null;
  // nameFilter = nameFilter;
  @tracked tosAgreed = false;

  get versionStyle() {
    const hasError = !this.session.isAuthenticated || this.tosError;
    return htmlSafe(`transform: translateY(${hasError ? '0' : '12px'})`);
  }

  @tracked carouselItems: CarouselItemData[] = [];
  private carouselElement: HTMLElement | null = null;
  private pageStarts: number[] = [];
  private carouselVisibleCount = 0;


  @tracked currentCarouselPage = 0;
  @tracked carouselPageCount = 0;


  private initializeCarousel() {
    // just compute layout once to clamp the width on first render
    this.getCarouselLayout();
  }

  private getCarouselElement(): HTMLElement | null {
    this.carouselElement = document.querySelector('[data-carousel]') as HTMLElement | null;
    return this.carouselElement;
  }

  get carouselDots(): number[] {
    return Array.from({ length: this.carouselPageCount }, (_, i) => i);
  }

  private getCarouselLayout() {
    const el = this.getCarouselElement();
    if (!el) return null;

    const track = el.querySelector('[data-carousel-track]') as HTMLElement | null;
    if (!track) return null;

    const items = track.querySelectorAll<HTMLElement>('[data-carousel-item]');
    if (items.length === 0) return null;
    const firstItem = items[0];
    const itemWidth = firstItem.offsetWidth;
    if (!itemWidth) return null;
    const styles = window.getComputedStyle(track);
    const gap =
      parseFloat(styles.columnGap || styles.gap || '0') || 0;

    const itemTotal = itemWidth + gap;

    // compute visibleCount once, then reuse
    let visibleCount = this.carouselVisibleCount;
    if (!visibleCount) {
      visibleCount = Math.max(
        1,
        Math.floor(el.clientWidth / itemTotal)
      );
      this.carouselVisibleCount = visibleCount;
    }

    const maxIndex = Math.max(0, items.length - visibleCount);

    // clamp the carousel viewport to exact content width of visible items
    const usedWidth = visibleCount * itemWidth + (visibleCount - 1) * gap;

    el.style.maxWidth = `${usedWidth}px`;
    el.style.width = `${usedWidth}px`;
    el.style.marginLeft = 'auto';
    el.style.marginRight = 'auto';

    // build page start indices (0-based item indexes)
    const pageStarts: number[] = [];
    for (let i = 0; i <= maxIndex; i += visibleCount) {
      pageStarts.push(i);
    }
    if (pageStarts[pageStarts.length - 1] !== maxIndex) {
      pageStarts.push(maxIndex);
    }

    this.pageStarts = pageStarts;
    this.carouselPageCount = pageStarts.length;

    return {
      el,
      itemTotal,
    };
  }

  @action
  scrollCarousel(direction: 'left' | 'right') {
    const layout = this.getCarouselLayout();
    if (!layout) return;

    const { el, itemTotal } = layout;
    const pageCount = this.carouselPageCount;
    if (!pageCount) return;

    if (direction === 'right') {
      this.currentCarouselPage = (this.currentCarouselPage + 1) % pageCount;
    } else {
      this.currentCarouselPage =
        (this.currentCarouselPage - 1 + pageCount) % pageCount;
    }

    const startIndex = this.pageStarts[this.currentCarouselPage] ?? 0;
    const left = startIndex * itemTotal;

    el.scrollTo({
      left,
      behavior: 'smooth',
    });
  }

  private static CAROUSEL_CACHE_KEY = 'bbmo_carousel_cache';

  private loadCachedCarouselItems(): boolean {
    try {
      const cached = localStorage.getItem(RestrictedController.CAROUSEL_CACHE_KEY);
      if (cached) {
        this.carouselItems = JSON.parse(cached) as CarouselItemData[];
        return true;
      }
    } catch (e) {
      // ignore parse errors
    }
    return false;
  }

  private async fetchCarouselItems(retries = 3): Promise<void> {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const url = macroCondition(isDevelopingApp()) ? CAROUSEL_JSON_URL_DEV : CAROUSEL_JSON_URL_PROD;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const items = (await res.json()) as CarouselItemData[];
        const resolved = items.map((item) => ({
          ...item,
          imgSrc: S3_IMAGES_BASE_URL + item.imgSrc,
        }));
        this.carouselItems = resolved;
        try {
          localStorage.setItem(RestrictedController.CAROUSEL_CACHE_KEY, JSON.stringify(resolved));
        } catch (e) {
          // ignore storage errors
        }
        return;
      } catch (err) {
        console.warn(`Carousel fetch attempt ${attempt}/${retries} failed`, err);
        if (attempt < retries) {
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
    }
    console.error('Failed to fetch carousel data after all retries');
  }


  get redirectTo() {
    return `${window.location.origin}/`;
  }

  constructor() {
    super(...arguments);

    const hadCache = this.loadCachedCarouselItems();
    if (hadCache) {
      scheduleOnce('afterRender', this, this.initializeCarousel);
    }
    this.fetchCarouselItems().then(() => {
      scheduleOnce('afterRender', this, this.initializeCarousel);
    });

    try {
      this.tosAgreed = localStorage.getItem(this.TOS_KEY) === '1';
    } catch (e) {
      console.error('Error accessing localStorage', e);
      this.tosAgreed = false;
    }
  }
  
  requireTosOrError(): boolean {
    if (!this.tosAgreed) {
      this.tosError = 'You must accept Terms and Conditions to use BeatBuddy Manager Online!';
      return false;
    }
    return true;
  }

  @action
  onTosChange(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.tosAgreed = checked;

    try {
      if (checked) {
        localStorage.setItem(this.TOS_KEY, '1');
        this.tosError = null;
      } else {
        localStorage.removeItem(this.TOS_KEY);
      }
    } catch (e) {
      console.error('Error accessing localStorage to set/remove TOS agreement', e);
    }
  }

  @action
  stopEvent(e: MouseEvent) {
    e?.stopPropagation();
  }

  @action
  toggleTos() {
    if (this.tosAgreed) {
      this.tosAgreed = false;
      localStorage.removeItem(this.TOS_KEY);
    } else {
      this.tosAgreed = true;
      localStorage.setItem(this.TOS_KEY, '1');
      this.tosError = null;
    }
  }

  get browserIsSupported() {
    const supportsDirectoryPicker = window.showDirectoryPicker !== undefined;
    const supportsNonDestructiveArrayMethods =
      Array.prototype.toSorted !== undefined;
    return supportsDirectoryPicker && supportsNonDestructiveArrayMethods;
  }

  @action
  async sync() {
    await this.projectManager.syncToSDCard();
  }

  @action
  async exportAll() {
    await this.projectManager.exportAll();
  }

  @action
  openProjectConversionModal() {
    this.showConversionModal = true;
  }

  @action
  async confirmConversion() {
    this.showConversionModal = false;
    try {
      await this.projectManager.setProjectDRMX();
    } catch (err: any) {
      if (err.name === 'AbortError') {
        this.showSkipBackupModal = true;
      } else {
        throw err;
      }
    }
  }

  @action
  cancelConversion() {
    this.showConversionModal = false;
  }

  @action
  confirmSkipBackup() {
    this.showSkipBackupModal = false;
    this.projectManager.convertWithoutBackup();
  }

  @action
  cancelSkipBackup() {
    this.showSkipBackupModal = false;
  }

  @action 
  async setProjectDevMode() {
    const password = prompt("Enter password:");
    if (!password) {
      return;
    }
    if (password !== "stay away influencers") {
      alert("Incorrect password");
      return;
    }
    await this.projectManager.setProjectDevMode();
  }

  @action
  async save() {
    await this.projectManager.save();
  }

  @action
  async toggleTheme() {
    await this.projectManager.toggleTheme(false);
  }

  @action
  showMenu(show: boolean, ev?: MouseEvent) {
    if (this.showOptionsMenu != show) {
      ev?.preventDefault();
      ev?.stopPropagation();
      this.showOptionsMenu = show;
    }
  }

  @action
  async importSong() {
    const currentSong = this.projectManager.currentSong;
    const commands = await this.projectManager.importSong();
    if (commands) {
      commands.on('execute', async (results) => {
        const [song] = results;
        if (song instanceof SongFile) {
          this.transitionToSong(song);
        }
        if (song instanceof SongFolder) {
          const folder = song as SongFolder;
          await waitForSongs(folder);
          this.transitionToFolder(song as SongFolder);
        }
      });
      commands.on('reverse', () => {
        if (currentSong) {
          this.transitionToSong(currentSong);
        } else {
          this.router.transitionTo('restricted.songs');
        }
      });

      await this.undoManager.executeCommand(commands);
    }
  }

  transitionToSong(song: SongFile) {
    if (
      this.router.isActive(
        'restricted.songs.folder.song',
        song.folder.path,
        song.path,
      )
    ) {
      this.router.refresh('restricted.songs.folder.song');
    } else {
      this.router.transitionTo(
        'restricted.songs.folder.song',
        song.folder.path,
        song.path,
      );
    }
  }

  transitionToFolder(folder: SongFolder) {
    if (this.router.isActive('restricted.songs.folder', folder.path)) {
      this.router.refresh('restricted.songs.folder');
    } else {
      this.router.transitionTo('restricted.songs.folder', folder.path);
    }
  }



   @action
   toggleProjectMenu() {
     if (this.tosError) {
       alert(this.tosError);
       return;
     }
    if (!this.requireTosOrError()) {
      return;
    }
     this.showProjectMenu = !this.showProjectMenu;
   }

  @action
  async importDrumset() {
    const command = await this.projectManager.importDrumset();
    if (command) {
      command.on('execute', (drumset) => {
        this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
      });
      command.on('reverse', () => {
        this.router.transitionTo('restricted.drumsets');
      });
      this.undoManager.executeCommand(command);
    }
  }

  @action
  async openProject() {
    if (this.tosError) {
      this.showProjectMenu = false;
      alert(this.tosError);
      return;
    }
    if (!this.requireTosOrError()) {
      this.showProjectMenu = false;
      return;
    }
    let handle: FileSystemDirectoryHandle;
    try {
      handle = await window.showDirectoryPicker({
        id: 'open-project',
        startIn: 'documents',
        mode: 'readwrite',
      });
    } catch (err: any) {
      if (err.name === 'AbortError' && /user aborted/i.test(err.message)) {
        return;
      }
      throw err;
    }
    this.projectManager.projectPickerId = 'open-project';
    if (await this.projectManager.loadProject(handle)) {
      localStorage.setItem('logs', '');
      const folder =
        this.projectManager.currentFolder ?? this.projectManager.songFolders[0];
      const song = this.projectManager.currentSong ?? folder?.songs[0];
      if (folder && song) {
        this.router.transitionTo(
          'restricted.songs.folder.song',
          folder.path,
          song.path,
        );
      } else {
        this.router.transitionTo('restricted.songs');
      }
    } else {
      alert('Invalid project');
    }
  }

  @action
  async newProject(isBB2Project: boolean) {
    if (this.tosError) {
      this.showProjectMenu = false;
      alert(this.tosError);
      return;
    }
    if (!this.requireTosOrError()) {
      this.showProjectMenu = false;
      return;
    }
    let projectDirectoryHandle: FileSystemDirectoryHandle;
    try {
      projectDirectoryHandle = await window.showDirectoryPicker({
        id: 'new-project',
        startIn: 'documents',
      });

      const entries: FileSystemHandle[] = [];
      for await (const [, entry] of projectDirectoryHandle.entries()) {
        entries.push(entry);
      }

      if (entries.length > 0) {
        alert('Please select an empty directory to create a new project in');
        return;
      }

      const projectFileHandle = await window.showSaveFilePicker({
        startIn: projectDirectoryHandle,
        suggestedName: projectDirectoryHandle.name + '.bbp',
        types: [
          {
            description: 'Beatbuddy Project',
            accept: {
              'application/x-beatbuddy-project': ['.bbp'],
            },
          },
        ],
      });

      if (!(await projectDirectoryHandle.resolve(projectFileHandle))) {
        alert('Please create a project file within the project directory');
        return;
      }
      this.projectManager.projectPickerId = 'new-project';
      await this.projectManager.createProject(
        projectDirectoryHandle,
        projectFileHandle,
        isBB2Project
      );
      this.router.transitionTo('restricted.songs');
    } catch (err: any) {
      if (err.name === 'AbortError' && /user aborted/i.test(err.message)) {
        return;
      }
      throw err;
    }
  }

  @action
  closeProject() {
    if (this.warnIfDirty()) {
      this.projectManager.closeProject();
      this.router.transitionTo('restricted.index');
    }
  }

  @action
  warnIfDirty(ev?: BeforeUnloadEvent): boolean {
    if (macroCondition(isDevelopingApp())) {
      // During development, I don’t want to be prompted to save changes when I refresh the page
      return true;
    }
    if (this.projectManager.isDirty) {
      const confirmed = confirm(
        'You have not saved your changes, are you sure you want to leave this page?\nClick "Cancel" to remain on this page.',
      );
      if (!confirmed) {
        if (ev) {
          ev.preventDefault();
          ev.returnValue = '';
        }
        return false;
      }
    }
    return true;
  }

  @action
  toggleHelpPopup(force: boolean) {
    this.displayHelpPopup = force;
  }

  @action
  goToAccountLink() {
    window.open('https://www.singularsound.com/customer_authentication/redirect', '_blank');
  }
  @action
  updateProjectName(name: string) {
    if (name.includes('/') || name.includes('\\')) {
      alert('Project name cannot contain slashes');
      return;
    }
    if (this.projectManager.projectFile) {
      this.undoManager.executeCommand(
        new RenameProjectCommand(this.projectManager, name),
      );
    }
  }
}
